import { useMemo } from 'react'

import { type Assignment, type Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { type Outcome } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'

import { getSelectableOutcomes } from './getMeasureRows'
import { getOutcomeLocalDateKey } from './outcomeDate'
import { type FlexibleAssessment } from '../data/flexibleAssessmentsApi'

export type AddedAssessmentResult = {
  assignment: Assignment
  outcome: Outcome
}

export type AddedAssessmentData = {
  id: string
  flexibleAssessment: FlexibleAssessment
  /** Program content metadata linked by the flexible assessment's content metadata id. */
  contentMetadata?: Content
  assignments: readonly Assignment[]
  /** Submitted outcomes for this assessment, newest first. */
  results: readonly AddedAssessmentResult[]
}

export type AddedAssessmentDisplayData = AddedAssessmentData & {
  /** Concise, unique suffix used by row controls and test ids. */
  testId: string
  /** True when the selected date contains multiple results for this assessment. */
  hasDuplicateAttempts: boolean
  /** Latest result by default, or one exact result completed on the selected date. */
  selectedResult?: AddedAssessmentResult
  isLatestView: boolean
}

export type AddedAssessmentDataSources = {
  flexibleAssessments?: readonly FlexibleAssessment[]
  assessmentContents?: readonly Content[]
  assignments?: readonly Assignment[]
  outcomes?: readonly Outcome[]
}

const getSourceKey = (episodeId: string, contentMetaDataId: string) => JSON.stringify([episodeId, contentMetaDataId])

const appendToGroup = <T>(groups: Map<string, T[]>, sourceKey: string, value: T) => {
  const existingGroup = groups.get(sourceKey)
  if (existingGroup) {
    existingGroup.push(value)
  } else {
    groups.set(sourceKey, [value])
  }
}

/**
 * Joins configured flexible assessments to their assignments and submitted outcomes.
 *
 * Flexible assessments and outcomes have no direct relationship, so assignments provide the
 * identity bridge. Results without an exact assignment id match are intentionally ignored; score
 * keys are not unique to flexible assessments and would incorrectly capture automated results.
 */
export const getAddedAssessmentsData = ({
  flexibleAssessments = [],
  assessmentContents = [],
  assignments = [],
  outcomes = [],
}: AddedAssessmentDataSources): AddedAssessmentData[] => {
  const contentMetadataById = new Map(
    assessmentContents.flatMap((contentMetadata) =>
      contentMetadata.id ? [[contentMetadata.id, contentMetadata] as const] : [],
    ),
  )
  const assignmentsBySource = new Map<string, Assignment[]>()
  const assignmentsById = new Map<string, Assignment>()

  for (const assignment of assignments) {
    const sourceKey = getSourceKey(assignment.episode_id, assignment.content_meta_data_id)
    appendToGroup(assignmentsBySource, sourceKey, assignment)
    assignmentsById.set(assignment.id, assignment)
  }

  const resultsBySource = new Map<string, AddedAssessmentResult[]>()
  for (const { outcome } of getSelectableOutcomes(outcomes)) {
    if (!outcome.assignment_id) continue

    const assignment = assignmentsById.get(outcome.assignment_id)
    if (!assignment) continue

    const sourceKey = getSourceKey(assignment.episode_id, assignment.content_meta_data_id)
    appendToGroup(resultsBySource, sourceKey, { assignment, outcome })
  }

  return flexibleAssessments.map((flexibleAssessment) => {
    const sourceKey = getSourceKey(flexibleAssessment.episode_id, flexibleAssessment.content_meta_data_id)
    return {
      id: flexibleAssessment.id,
      flexibleAssessment,
      contentMetadata: contentMetadataById.get(flexibleAssessment.content_meta_data_id),
      assignments: assignmentsBySource.get(sourceKey) ?? [],
      results: resultsBySource.get(sourceKey) ?? [],
    }
  })
}

/** Selects a result for display without removing configured assessments that have no match. */
export const selectAddedAssessmentResults = (
  data: readonly AddedAssessmentData[],
  selectedDateKey?: string,
): AddedAssessmentDisplayData[] => {
  const isLatestView = selectedDateKey === undefined

  return data.flatMap((assessment) => {
    const matchingResults = isLatestView
      ? assessment.results.slice(0, 1)
      : assessment.results.filter(({ outcome }) => getOutcomeLocalDateKey(outcome.response_date) === selectedDateKey)
    const resultsToDisplay: Array<AddedAssessmentResult | undefined> = matchingResults.length
      ? matchingResults
      : [undefined]

    return resultsToDisplay.map((selectedResult, index) => ({
      ...assessment,
      testId: matchingResults.length > 1 ? `${assessment.id}-attempt-${index + 1}` : assessment.id,
      hasDuplicateAttempts: matchingResults.length > 1,
      selectedResult,
      isLatestView,
    }))
  })
}

export const useAddedAssessmentsData = (dataSources: AddedAssessmentDataSources): AddedAssessmentData[] => {
  const { flexibleAssessments, assessmentContents, assignments, outcomes } = dataSources
  return useMemo(
    () => getAddedAssessmentsData({ flexibleAssessments, assessmentContents, assignments, outcomes }),
    [assessmentContents, assignments, flexibleAssessments, outcomes],
  )
}
